import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { getProjectVitalFinanceOverview } from '../services/projectVitalService';
import {
  createPVProfessionalSuggestedReserves,
  getPVProfessionalMonthlyControl,
  getPVProfessionalSettings,
  updatePVProfessionalSettings,
} from '../services/projectVitalProfessionalControlService';
import type {
  PVProfessionalActivityStatus,
  PVProfessionalFranceConventionStatus,
  PVProfessionalProjectionMethod,
  PVProfessionalSettings,
  PVProfessionalSettingsWrite,
  PVProfessionalTaxEstimationMode,
  PVProfessionalTaxReserveBasis,
  PVProfessionalVatTreatment,
} from '../types/projectVitalProfessionalControl';

const CYAN = '#4DD0E1';
const GREEN = '#4ade80';
const RED = '#fca5a5';
const AMBER = '#fcd34d';

function localToday(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString().slice(0, 10);
}

function currentMonth(): string {
  return localToday().slice(0, 7);
}

function euro(value: number | string): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function decimalText(value: string, allowZero = true): string | null {
  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const number = Number(normalized);
  if (!Number.isFinite(number) || number > 999999999999.99) return null;
  if (!allowZero && number <= 0) return null;
  if (number < 0) return null;
  return number.toFixed(2);
}

function errorText(error: unknown): string {
  const candidate = error as { response?: { data?: { detail?: unknown } } };
  if (typeof candidate?.response?.data?.detail === 'string') {
    return candidate.response.data.detail;
  }
  return 'No se ha podido completar la operación.';
}

const statusLabel: Record<PVProfessionalActivityStatus, string> = {
  pre_activity: 'Preparando actividad',
  active: 'Actividad activa',
  paused: 'Actividad pausada',
};

const basisLabel: Record<PVProfessionalTaxReserveBasis, string> = {
  disabled: 'Sin cálculo orientativo',
  professional_income: 'Ingresos profesionales cobrados',
  issued_base: 'Base de facturas emitidas',
};

const projectionLabel: Record<PVProfessionalProjectionMethod, string> = {
  manual_target: 'Objetivo anual manual',
  adaptive: 'Proyección dinámica',
  run_rate: 'Proyección dinámica (legado)',
};

const emptySettings: PVProfessionalSettingsWrite = {
  activity_status: 'pre_activity',
  activity_name: null,
  activity_start_date: null,
  tax_estimation_mode: 'simple',
  annual_revenue_target: '0.00',
  annual_revenue_target_year: new Date().getFullYear(),
  projection_method: 'manual_target',
  additional_planned_professional_expenses: '0.00',
  household_status: 'single',
  dependent_children: 0,
  activity_year_number: 1,
  vat_treatment: 'exempt_healthcare',
  france_convention_status: 'unknown',
  other_taxable_income_annual: '0.00',
  tax_reserve_basis: 'disabled',
  tax_reserve_percent: '0.00',
  fixed_monthly_commitment_label: null,
  fixed_monthly_commitment_amount: '0.00',
  default_reserve_account_id: null,
  notes: null,
};

function settingsToForm(settings: PVProfessionalSettings): PVProfessionalSettingsWrite {
  return {
    activity_status: settings.activity_status,
    activity_name: settings.activity_name,
    activity_start_date: settings.activity_start_date,
    tax_estimation_mode: settings.tax_estimation_mode,
    annual_revenue_target: String(settings.annual_revenue_target),
    annual_revenue_target_year: settings.annual_revenue_target_year,
    projection_method: settings.projection_method === 'run_rate' ? 'adaptive' : settings.projection_method,
    additional_planned_professional_expenses: String(settings.additional_planned_professional_expenses ?? '0.00'),
    household_status: settings.household_status,
    dependent_children: settings.dependent_children,
    activity_year_number: settings.activity_year_number,
    vat_treatment: settings.vat_treatment,
    france_convention_status: settings.france_convention_status,
    other_taxable_income_annual: String(settings.other_taxable_income_annual),
    tax_reserve_basis: settings.tax_reserve_basis,
    tax_reserve_percent: String(settings.tax_reserve_percent),
    fixed_monthly_commitment_label: settings.fixed_monthly_commitment_label,
    fixed_monthly_commitment_amount: String(settings.fixed_monthly_commitment_amount),
    default_reserve_account_id: settings.default_reserve_account_id,
    notes: settings.notes,
  };
}

function MetricCard({
  label,
  value,
  tone = 'default',
  helper,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'positive' | 'warning' | 'danger';
  helper?: string;
}) {
  const color = tone === 'positive'
    ? GREEN
    : tone === 'warning'
      ? AMBER
      : tone === 'danger'
        ? RED
        : '#f8fafc';

  return (
    <Box
      sx={{
        p: 1.45,
        borderRadius: 2.6,
        bgcolor: alpha('#ffffff', 0.025),
        border: `1px solid ${alpha('#94a3b8', 0.12)}`,
        minWidth: 0,
      }}
    >
      <Typography sx={{ color: '#94a3b8', fontSize: '.73rem', fontWeight: 750 }}>
        {label}
      </Typography>
      <Typography sx={{ mt: .45, color, fontSize: '1.04rem', fontWeight: 900 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography sx={{ mt: .35, color: '#64748b', fontSize: '.7rem', lineHeight: 1.35 }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function DetailRow({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
      <Typography sx={{ color: strong ? '#e2e8f0' : '#94a3b8', fontSize: '.8rem', fontWeight: strong ? 800 : 500 }}>
        {label}
      </Typography>
      <Typography sx={{ color: strong ? '#f8fafc' : '#cbd5e1', fontSize: strong ? '.94rem' : '.82rem', fontWeight: strong ? 900 : 750, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

type Props = {
  fiscalOnly?: boolean;
  month?: string;
  onMonthChange?: (month: string) => void;
};

export default function ProjectVitalProfessionalControlSection({
  fiscalOnly = false,
  month: controlledMonth,
  onMonthChange,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [internalMonth, setInternalMonth] = useState(currentMonth());
  const month = controlledMonth ?? internalMonth;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [form, setForm] = useState<PVProfessionalSettingsWrite>(emptySettings);
  const [formError, setFormError] = useState('');
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveAccountId, setReserveAccountId] = useState('');
  const [reserveDueDate, setReserveDueDate] = useState(localToday());
  const [includeTax, setIncludeTax] = useState(true);
  const [includeFixed, setIncludeFixed] = useState(true);
  const [reserveError, setReserveError] = useState('');
  const [showFiscalDetails, setShowFiscalDetails] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ['project-vital', 'professional-settings'],
    queryFn: getPVProfessionalSettings,
  });
  const controlQuery = useQuery({
    queryKey: ['project-vital', 'professional-monthly-control', month],
    queryFn: () => getPVProfessionalMonthlyControl(month),
  });
  const accountsQuery = useQuery({
    queryKey: ['project-vital', 'finance', currentMonth(), 'global'],
    queryFn: () => getProjectVitalFinanceOverview(currentMonth(), 'global'),
  });

  const activeAccounts = useMemo(
    () => (accountsQuery.data?.accounts ?? []).filter((account) => account.is_active),
    [accountsQuery.data],
  );
  const professionalAccounts = useMemo(
    () => activeAccounts.filter((account) => account.financial_scope === 'professional'),
    [activeAccounts],
  );

  useEffect(() => {
    const settings = settingsQuery.data;
    if (!settings || settingsOpen) return;
    setForm(settingsToForm(settings));
  }, [settingsQuery.data, settingsOpen]);

  const saveSettingsMutation = useMutation({
    mutationFn: updatePVProfessionalSettings,
    onSuccess: async () => {
      setSettingsOpen(false);
      setForecastOpen(false);
      setFormError('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-settings'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-monthly-control'] }),
      ]);
      enqueueSnackbar('Configuración profesional guardada.', { variant: 'success' });
    },
    onError: (error) => setFormError(errorText(error)),
  });

  const createReservesMutation = useMutation({
    mutationFn: () => createPVProfessionalSuggestedReserves(month, {
      account_id: reserveAccountId,
      due_date: reserveDueDate,
      include_tax_reserve: includeTax,
      include_fixed_commitment: includeFixed,
    }),
    onSuccess: async (result) => {
      setReserveOpen(false);
      setReserveError('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-reserves'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-monthly-control'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-cashflow'] }),
      ]);

      const parts = [`${result.created_count} creada(s)`];
      if (result.updated_count > 0) parts.push(`${result.updated_count} actualizada(s)`);
      if (result.existing_count > 0) parts.push(`${result.existing_count} ya estaba(n) al día`);
      enqueueSnackbar(`Reservas: ${parts.join(', ')}.`, { variant: 'success' });
    },
    onError: (error) => setReserveError(errorText(error)),
  });

  function openSettings() {
    setForm(settingsQuery.data ? settingsToForm(settingsQuery.data) : emptySettings);
    setFormError('');
    setSettingsOpen(true);
  }

  function openForecastAdjustment() {
    setForm(settingsQuery.data ? settingsToForm(settingsQuery.data) : emptySettings);
    setFormError('');
    setForecastOpen(true);
  }

  function saveSettings() {
    const percent = decimalText(String(form.tax_reserve_percent));
    const fixed = decimalText(String(form.fixed_monthly_commitment_amount));
    const annualTarget = decimalText(String(form.annual_revenue_target));
    const otherIncome = decimalText(String(form.other_taxable_income_annual));
    const plannedExpenses = decimalText(String(form.additional_planned_professional_expenses));

    if (percent === null || fixed === null || annualTarget === null || otherIncome === null || plannedExpenses === null) {
      setFormError('Revisa los importes y porcentajes indicados.');
      return;
    }
    if (form.tax_estimation_mode === 'simple' && form.tax_reserve_basis !== 'disabled' && Number(percent) <= 0) {
      setFormError('La regla simple de reserva necesita un porcentaje mayor que cero.');
      return;
    }
    if (Number(annualTarget) > 0 && (form.annual_revenue_target_year === null || !Number.isInteger(Number(form.annual_revenue_target_year)) || Number(form.annual_revenue_target_year) < 2000 || Number(form.annual_revenue_target_year) > 2100)) {
      setFormError('Indica el año al que corresponde el objetivo anual.');
      return;
    }
    if (form.tax_estimation_mode === 'france_dentist_bnc' && form.projection_method === 'manual_target' && Number(annualTarget) <= 0) {
      setFormError('Indica un objetivo anual mayor que cero o utiliza la proyección dinámica.');
      return;
    }
    if (!Number.isInteger(Number(form.activity_year_number)) || Number(form.activity_year_number) < 1) {
      setFormError('El año de actividad debe ser un número entero mayor o igual que 1.');
      return;
    }
    if (Number(fixed) > 0 && !form.fixed_monthly_commitment_label?.trim()) {
      setFormError('Indica el concepto del compromiso fijo mensual.');
      return;
    }

    saveSettingsMutation.mutate({
      ...form,
      activity_name: form.activity_name?.trim() || null,
      annual_revenue_target: annualTarget,
      annual_revenue_target_year: form.annual_revenue_target_year,
      additional_planned_professional_expenses: plannedExpenses,
      household_status: 'single',
      dependent_children: 0,
      activity_year_number: Number(form.activity_year_number),
      other_taxable_income_annual: otherIncome,
      tax_reserve_basis: form.tax_estimation_mode === 'france_dentist_bnc'
        ? 'disabled'
        : form.tax_reserve_basis,
      tax_reserve_percent: form.tax_estimation_mode === 'france_dentist_bnc' || form.tax_reserve_basis === 'disabled'
        ? '0.00'
        : percent,
      fixed_monthly_commitment_label: form.fixed_monthly_commitment_label?.trim() || null,
      fixed_monthly_commitment_amount: fixed,
      notes: form.notes?.trim() || null,
    });
  }

  const control = controlQuery.data;
  const fiscal = control?.fiscal_estimate ?? null;
  const franceMode = control?.tax_estimation_mode === 'france_dentist_bnc';
  const suggestedPositive = Number(control?.suggested_total_reserve ?? 0) > 0;

  function applyDynamicProjection() {
    if (!settingsQuery.data) return;
    const current = settingsToForm(settingsQuery.data);
    saveSettingsMutation.mutate({
      ...current,
      projection_method: 'adaptive',
    });
  }

  function openSuggestedReserves() {
    const preferred = control?.default_reserve_account_id
      || professionalAccounts[0]?.id
      || activeAccounts[0]?.id
      || '';
    setReserveAccountId(preferred);
    setReserveDueDate(localToday());
    setIncludeTax(Number(control?.suggested_tax_reserve ?? 0) > 0);
    setIncludeFixed(Number(control?.fixed_monthly_commitment_amount ?? 0) > 0);
    setReserveError('');
    setReserveOpen(true);
  }

  return (
    <Box
      sx={{
        mt: fiscalOnly ? 0 : 2,
        p: { xs: 1.7, md: 2.2 },
        borderRadius: 3.5,
        bgcolor: '#0e1a2d',
        border: `1px solid ${alpha(CYAN, 0.13)}`,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoGraphRoundedIcon sx={{ color: CYAN }} />
            <Typography sx={{ color: '#f8fafc', fontSize: '1.05rem', fontWeight: 900 }}>
              {fiscalOnly ? 'Estimación fiscal' : 'Control profesional'}
            </Typography>
          </Stack>
          <Typography sx={{ mt: .4, color: '#64748b', fontSize: '.79rem' }}>
            {fiscalOnly
              ? 'Proyección anual y reserva recomendada según la situación registrada.'
              : 'Caja, obligaciones y dinero realmente disponible de la actividad.'}
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            type="month"
            label="Mes"
            InputLabelProps={{ shrink: true }}
            value={month}
            onChange={(event) => {
              const value = event.target.value;
              if (onMonthChange) onMonthChange(value);
              else setInternalMonth(value);
            }}
          />
          <Button startIcon={<SettingsRoundedIcon />} variant="outlined" onClick={openSettings}>
            Configurar
          </Button>
        </Stack>
      </Stack>

      {(settingsQuery.isLoading || controlQuery.isLoading || accountsQuery.isLoading) && (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {(settingsQuery.isError || controlQuery.isError || accountsQuery.isError) && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          No se ha podido cargar el control profesional.
        </Alert>
      )}

      {control && !controlQuery.isLoading && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.7, mb: 1.2, flexWrap: 'wrap', rowGap: .8 }}>
            <Box sx={{ px: 1.1, py: .55, borderRadius: 99, bgcolor: alpha(CYAN, .09), border: `1px solid ${alpha(CYAN, .22)}` }}>
              <Typography sx={{ color: CYAN, fontSize: '.72rem', fontWeight: 850 }}>
                {statusLabel[control.activity_status]}
              </Typography>
            </Box>
            <Typography sx={{ color: '#64748b', fontSize: '.75rem' }}>
              {franceMode ? 'Estimación fiscal Francia · dentista BNC' : `Regla de reserva: ${basisLabel[control.tax_reserve_basis]}`}
            </Typography>
          </Stack>

          {!fiscalOnly ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              <MetricCard label="Ingresos del mes" value={euro(control.professional_income)} tone="positive" />
              <MetricCard label="Gastos del mes" value={euro(control.professional_expenses)} tone="danger" />
              <MetricCard label="Balance de caja" value={euro(control.professional_cash_balance)} tone={Number(control.professional_cash_balance) >= 0 ? 'positive' : 'danger'} />
              <MetricCard label="Liquidez profesional" value={euro(control.professional_liquidity)} />
            </Box>
          ) : null}

          <Box
            sx={{
              mt: 1.2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: fiscalOnly ? '1fr' : franceMode ? 'minmax(0, .78fr) minmax(0, 1.22fr)' : '1fr 1fr' },
              gap: 1.2,
            }}
          >
            {!fiscalOnly ? (
              <Box sx={{ p: 1.6, borderRadius: 2.8, bgcolor: alpha('#ffffff', .022), border: `1px solid ${alpha('#94a3b8', .11)}` }}>
                <Typography sx={{ color: '#f8fafc', fontSize: '.88rem', fontWeight: 850 }}>
                  Pendientes y disponibilidad
                </Typography>
                <Stack spacing={.75} mt={1.15}>
                  <DetailRow label="Pendiente de cobrar" value={euro(control.current_receivables)} />
                  <DetailRow label="Pendiente de pagar" value={euro(control.current_payables)} />
                  <DetailRow label="Reservas pendientes" value={euro(control.pending_reserves_total)} />
                  <DetailRow label="Reservas con vencimiento este mes" value={euro(control.reserves_due_in_month)} />
                  {!franceMode ? (
                    <DetailRow label="Disponible global tras reservas" value={euro(control.available_after_reserves)} strong />
                  ) : null}
                </Stack>

                <Typography sx={{ mt: 1.55, color: '#64748b', fontSize: '.72rem' }}>
                  Facturado: {euro(control.issued_total)} · Cobrado por facturas: {euro(control.invoice_collections)}
                </Typography>
              </Box>
            ) : null}

            {franceMode && fiscal ? (
              <Box sx={{ p: 1.7, borderRadius: 2.8, bgcolor: alpha(CYAN, .035), border: `1px solid ${alpha(CYAN, .18)}` }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.2}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={.8}>
                      <AccountBalanceRoundedIcon sx={{ color: CYAN, fontSize: 20 }} />
                      <Typography sx={{ color: '#f8fafc', fontSize: '.92rem', fontWeight: 900 }}>
                        Estimación fiscal Francia · {fiscal.year}
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: .4, color: '#64748b', fontSize: '.72rem' }}>
                      {projectionLabel[fiscal.projection_method]} · {fiscal.rules_version}
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={.7}>
                    <Button size="small" variant="outlined" onClick={openForecastAdjustment}>
                      Ajustar previsión
                    </Button>
                    <Button
                      size="small"
                      startIcon={<SavingsRoundedIcon />}
                      disabled={!suggestedPositive || activeAccounts.length === 0}
                      onClick={openSuggestedReserves}
                    >
                      Sincronizar reserva fiscal
                    </Button>
                  </Stack>
                </Stack>
                {fiscalOnly ? (
                  <>
                    <Box
                      sx={{
                        mt: 1.35,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                        gap: .9,
                      }}
                    >
                      <MetricCard label="Objetivo anual" value={euro(fiscal.annual_revenue_target)} />
                      <MetricCard
                        label="Proyección dinámica"
                        value={euro(fiscal.dynamic_projected_annual_revenue)}
                        helper={`Confianza ${Number(fiscal.projection_confidence_percent).toFixed(0)} %`}
                      />
                      <MetricCard
                        label="Base usada para fiscalidad"
                        value={euro(fiscal.projected_annual_revenue)}
                        helper={projectionLabel[fiscal.projection_method]}
                      />
                      <MetricCard label="Obligaciones anuales" value={euro(fiscal.total_obligations_estimate)} tone="warning" />
                    </Box>

                    {fiscal.projection_suggestion_level !== 'none' ? (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={0.5}
                        sx={{ mt: 0.85, px: 0.2 }}
                      >
                        <Typography sx={{ color: '#d4a85b', fontSize: '.72rem', lineHeight: 1.4 }}>
                          Desviación respecto al objetivo:{' '}
                          <strong>
                            {Number(fiscal.projection_deviation_amount) < 0 ? '−' : '+'}
                            {Math.abs(Number(fiscal.projection_deviation_percent)).toFixed(1)} %
                          </strong>
                          {' · '}
                          {Number(fiscal.projection_deviation_amount) < 0 ? '−' : '+'}
                          {euro(Math.abs(Number(fiscal.projection_deviation_amount)))}
                        </Typography>

                        {fiscal.projection_method === 'manual_target' ? (
                          <Button
                            size="small"
                            disabled={saveSettingsMutation.isPending}
                            onClick={applyDynamicProjection}
                            sx={{ textTransform: 'none', fontSize: '.72rem' }}
                          >
                            Usar proyección dinámica
                          </Button>
                        ) : null}
                      </Stack>
                    ) : null}

                    <Box
                      sx={{
                        mt: 1.05,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: .9,
                      }}
                    >
                      <Box sx={{ p: 1.25, borderRadius: 2.5, bgcolor: alpha('#020617', .24), border: `1px solid ${alpha('#94a3b8', .09)}` }}>
                        <Typography sx={{ color: '#e2e8f0', fontWeight: 850, fontSize: '.78rem', mb: .8 }}>
                          Ingresos · evolución
                        </Typography>
                        <Stack spacing={.62}>
                          <DetailRow label="Cobrado este año" value={euro(fiscal.income_ytd)} />
                          <DetailRow label="Ingresos futuros ya planificados" value={euro(fiscal.planned_professional_income_remaining)} />
                          <DetailRow label="Media mensual reciente" value={euro(fiscal.recent_average_monthly_income)} />
                          <DetailRow label="Ritmo anual simple" value={euro(fiscal.run_rate_revenue)} />
                        </Stack>
                      </Box>

                      <Box sx={{ p: 1.25, borderRadius: 2.5, bgcolor: alpha('#020617', .24), border: `1px solid ${alpha('#94a3b8', .09)}` }}>
                        <Typography sx={{ color: '#e2e8f0', fontWeight: 850, fontSize: '.78rem', mb: .8 }}>
                          Gastos profesionales · previsión
                        </Typography>
                        <Stack spacing={.62}>
                          <DetailRow label="Gastos operativos reales" value={euro(fiscal.operating_expenses_ytd)} />
                          <DetailRow label="Gastos futuros en Planificación" value={euro(fiscal.planned_professional_expenses_remaining)} />
                          <DetailRow label="Ajuste manual adicional" value={euro(fiscal.manual_planned_professional_expenses)} />
                          <DetailRow label="Gasto anual proyectado" value={euro(fiscal.projected_operating_expenses)} strong />
                        </Stack>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 1.05, p: 1.25, borderRadius: 2.5, bgcolor: alpha('#020617', .24), border: `1px solid ${alpha('#94a3b8', .09)}` }}>
                      <Typography sx={{ color: '#e2e8f0', fontWeight: 850, fontSize: '.78rem', mb: .8 }}>
                        Obligaciones y reserva
                      </Typography>
                      <Stack spacing={.62}>
                        <DetailRow label="Cotizaciones sociales estimadas" value={euro(fiscal.total_social_estimate)} />
                        <DetailRow label="IR estimado" value={euro(fiscal.income_tax_estimate)} />
                        {Number(fiscal.high_income_reference_estimate) > 250000 ? (
                          <>
                            <DetailRow label="CEHR estimada" value={euro(fiscal.cehr_estimate)} />
                            <DetailRow label="CDHR estimada" value={euro(fiscal.cdhr_estimate)} />
                          </>
                        ) : null}
                        <DetailRow label="Total fiscal + social anual" value={euro(fiscal.total_obligations_estimate)} strong />
                        <DetailRow label="Pagos fiscales/sociales ya registrados" value={euro(fiscal.fiscal_payments_ytd)} />
                        <DetailRow label="Reserva recomendada acumulada hoy" value={euro(fiscal.reserve_target)} />
                        <DetailRow label="Ya protegido en reservas fiscales" value={euro(fiscal.fiscal_pending_reserves)} />
                        <DetailRow label="Falta por reservar ahora" value={euro(fiscal.additional_reserve_needed)} />
                        <DetailRow label="Disponible profesional estimado" value={euro(fiscal.estimated_available_professional)} strong />
                      </Stack>
                    </Box>

                    <Button
                      size="small"
                      onClick={() => setShowFiscalDetails((value) => !value)}
                      sx={{ mt: .8, textTransform: 'none' }}
                    >
                      {showFiscalDetails ? 'Ocultar detalle del cálculo' : 'Ver detalle del cálculo'}
                    </Button>

                    <Collapse in={showFiscalDetails}>
                      <Box sx={{ mt: .75, p: 1.25, borderRadius: 2.5, bgcolor: alpha('#ffffff', .018), border: `1px solid ${alpha('#94a3b8', .08)}` }}>
                        <Stack spacing={.62}>
                          <DetailRow label="Resultado profesional proyectado" value={euro(fiscal.projected_result_before_social)} />
                          <DetailRow label="Base social estimada" value={euro(fiscal.social_assessment_base)} />
                          <DetailRow label="URSSAF estimada" value={euro(fiscal.urssaf_total)} />
                          <DetailRow label="CARCDSF estimada" value={euro(fiscal.carcdsf_total)} />
                          <DetailRow label="Renta imponible estimada" value={euro(fiscal.taxable_income_estimate)} />
                          <DetailRow label="Referencia altos ingresos aproximada" value={euro(fiscal.high_income_reference_estimate)} />
                          <DetailRow
                            label="Ratio orientativo sobre ingresos proyectados"
                            value={`${Number(fiscal.projected_annual_revenue) > 0 ? ((Number(fiscal.total_obligations_estimate) / Number(fiscal.projected_annual_revenue)) * 100).toFixed(1) : '0.0'} %`}
                          />
                        </Stack>
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Un gasto profesional adicional de 100 € reduce las obligaciones estimadas en aproximadamente{' '}
                          <strong>{euro(fiscal.estimated_obligation_saving_per_100_expense)}</strong>. Su coste económico neto estimado sería{' '}
                          <strong>{euro(fiscal.estimated_net_cost_per_100_expense)}</strong>. La salida bancaria sigue siendo 100 €.
                        </Alert>
                        <Stack spacing={.55} sx={{ mt: 1 }}>
                          {fiscal.warnings.map((warning) => (
                            <Typography key={warning} sx={{ color: '#94a3b8', fontSize: '.7rem', lineHeight: 1.4 }}>
                              • {warning}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    </Collapse>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        mt: 1.35,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' },
                        gap: .9,
                      }}
                    >
                      <MetricCard label="Cobrado este año" value={euro(fiscal.income_ytd)} />
                      <MetricCard label="Proyección anual" value={euro(fiscal.projected_annual_revenue)} helper={fiscal.projection_method === 'manual_target' ? 'Objetivo configurado' : `Ritmo observado: ${euro(fiscal.run_rate_revenue)}`} />
                      <MetricCard label="Gastos operativos proyectados" value={euro(fiscal.projected_operating_expenses)} />
                      <MetricCard label="Resultado proyectado" value={euro(fiscal.projected_result_before_social)} />
                      <MetricCard label="Cotizaciones estimadas" value={euro(fiscal.total_social_estimate)} tone="warning" helper={`URSSAF ${euro(fiscal.urssaf_total)} · CARCDSF ${euro(fiscal.carcdsf_total)}`} />
                      <MetricCard label="IR estimado" value={euro(fiscal.income_tax_estimate)} tone="warning" />
                    </Box>

                    <Box sx={{ mt: 1.25, p: 1.35, borderRadius: 2.5, bgcolor: alpha('#020617', .24), border: `1px solid ${alpha('#94a3b8', .09)}` }}>
                      <Stack spacing={.62}>
                        <DetailRow label="Obligaciones anuales proyectadas" value={euro(fiscal.total_obligations_estimate)} />
                        <DetailRow
                          label="Ratio orientativo sobre ingresos proyectados"
                          value={`${Number(fiscal.projected_annual_revenue) > 0 ? ((Number(fiscal.total_obligations_estimate) / Number(fiscal.projected_annual_revenue)) * 100).toFixed(1) : '0.0'} %`}
                        />
                        <DetailRow label="Pagos fiscales/sociales ya registrados" value={euro(fiscal.fiscal_payments_ytd)} />
                        <DetailRow label="Reserva recomendada acumulada hoy" value={euro(fiscal.reserve_target)} />
                        <DetailRow label="Ya protegido en reservas fiscales" value={euro(fiscal.fiscal_pending_reserves)} />
                        <DetailRow label="Falta por reservar" value={euro(fiscal.additional_reserve_needed)} />
                        <DetailRow label="Disponible profesional estimado" value={euro(fiscal.estimated_available_professional)} strong />
                      </Stack>
                    </Box>

                    <Alert severity="info" sx={{ mt: 1.15 }}>
                      Un gasto profesional adicional de 100 € reduce las obligaciones estimadas en aproximadamente{' '}
                      <strong>{euro(fiscal.estimated_obligation_saving_per_100_expense)}</strong>. Su coste económico neto estimado sería{' '}
                      <strong>{euro(fiscal.estimated_net_cost_per_100_expense)}</strong>. La salida bancaria sigue siendo 100 €.
                    </Alert>

                    <Stack spacing={.7} sx={{ mt: 1.1 }}>
                      {fiscal.warnings.map((warning) => (
                        <Typography key={warning} sx={{ color: '#94a3b8', fontSize: '.7rem', lineHeight: 1.4 }}>
                          • {warning}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 1.6, borderRadius: 2.8, bgcolor: alpha(AMBER, .025), border: `1px solid ${alpha(AMBER, .15)}` }}>
                <Typography sx={{ color: '#f8fafc', fontSize: '.88rem', fontWeight: 850 }}>
                  Reserva orientativa
                </Typography>
                <Stack spacing={.75} mt={1.15}>
                  <DetailRow label="Base de la regla" value={euro(control.tax_reserve_base)} />
                  <DetailRow label="Reserva fiscal sugerida" value={euro(control.suggested_tax_reserve)} />
                  <DetailRow label={control.fixed_monthly_commitment_label || 'Compromiso fijo'} value={euro(control.fixed_monthly_commitment_amount)} />
                  <DetailRow label="Total sugerido" value={euro(control.suggested_total_reserve)} strong />
                </Stack>
                <Button
                  size="small"
                  sx={{ mt: 1.2 }}
                  disabled={!suggestedPositive || activeAccounts.length === 0}
                  onClick={openSuggestedReserves}
                >
                  Crear reservas sugeridas
                </Button>
              </Box>
            )}
          </Box>

          {!fiscalOnly ? (
            <Alert severity="info" sx={{ mt: 1.4 }}>
              La cuenta indica dónde está el dinero; Personal/Profesional indica su naturaleza económica. Al registrar un movimiento desde la vista Profesional se propone como profesional, aunque la cuenta de pago pueda ser personal.
            </Alert>
          ) : null}
        </>
      )}

      <Dialog
        open={forecastOpen}
        maxWidth="sm"
        fullWidth
        onClose={() => !saveSettingsMutation.isPending && setForecastOpen(false)}
      >
        <DialogTitle>Ajustar previsión fiscal</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: .5 }}>
            El objetivo anual no cambia solo. La proyección dinámica se recalcula con los cobros reales y la planificación; aquí decides qué base usar para fiscalidad y puedes añadir gastos futuros todavía no planificados.
          </Alert>
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              label="Objetivo anual de ingresos (€)"
              inputProps={{ inputMode: 'decimal' }}
              value={form.annual_revenue_target}
              onChange={(event) => setForm((current) => ({ ...current, annual_revenue_target: event.target.value }))}
            />
            <TextField
              fullWidth type="number" size="small" label="Año del objetivo"
              inputProps={{ min: 2000, max: 2100, inputMode: 'numeric' }}
              value={form.annual_revenue_target_year ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, annual_revenue_target_year: event.target.value ? Number(event.target.value) : null }))}
              helperText="Evita trasladar por error una previsión parcial de 2026 a ejercicios futuros."
            />
            <FormControl fullWidth>
              <InputLabel>Base usada para fiscalidad</InputLabel>
              <Select
                value={form.projection_method}
                label="Base usada para fiscalidad"
                onChange={(event) => setForm((current) => ({
                  ...current,
                  projection_method: event.target.value as PVProfessionalProjectionMethod,
                }))}
              >
                <MenuItem value="manual_target">Objetivo anual</MenuItem>
                <MenuItem value="adaptive">Proyección dinámica</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Gastos futuros adicionales (€)"
              inputProps={{ inputMode: 'decimal' }}
              value={form.additional_planned_professional_expenses}
              onChange={(event) => setForm((current) => ({
                ...current,
                additional_planned_professional_expenses: event.target.value,
              }))}
              helperText="No incluyas aquí los gastos que ya estén creados en Planificación: se incorporan automáticamente."
            />
            {fiscal ? (
              <Box sx={{ p: 1.2, borderRadius: 2.3, bgcolor: alpha('#ffffff', .02) }}>
                <DetailRow label="Proyección dinámica actual" value={euro(fiscal.dynamic_projected_annual_revenue)} />
                <DetailRow label="Gastos anuales proyectados" value={euro(fiscal.projected_operating_expenses)} />
              </Box>
            ) : null}
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saveSettingsMutation.isPending} onClick={() => setForecastOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={saveSettingsMutation.isPending} onClick={saveSettings}>
            {saveSettingsMutation.isPending ? 'Guardando...' : 'Actualizar cálculo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={settingsOpen}
        maxWidth="md"
        fullWidth
        onClose={() => !saveSettingsMutation.isPending && setSettingsOpen(false)}
      >
        <DialogTitle>Configuración profesional</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: .5 }}>
            Puedes usar una reserva porcentual simple o una estimación orientativa para dentista liberal BNC en Francia. Esta última sirve para planificación de tesorería, no como liquidación oficial.
          </Alert>

          <Stack spacing={1.6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
              <FormControl fullWidth>
                <InputLabel>Situación de la actividad</InputLabel>
                <Select
                  value={form.activity_status}
                  label="Situación de la actividad"
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    activity_status: event.target.value as PVProfessionalActivityStatus,
                  }))}
                >
                  {(Object.keys(statusLabel) as PVProfessionalActivityStatus[]).map((value) => (
                    <MenuItem key={value} value={value}>{statusLabel[value]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Nombre de la actividad"
                value={form.activity_name ?? ''}
                onChange={(event) => setForm((current) => ({ ...current, activity_name: event.target.value }))}
                placeholder="Ej. Dentista liberal"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
              <TextField
                fullWidth
                label="Fecha de inicio"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.activity_start_date ?? ''}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  activity_start_date: event.target.value || null,
                }))}
              />
              <TextField
                fullWidth
                label="Año de actividad"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={form.activity_year_number}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  activity_year_number: Number(event.target.value || 1),
                }))}
                helperText="1 = primer año como autónoma/profesional liberal."
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Modo de estimación fiscal</InputLabel>
              <Select
                value={form.tax_estimation_mode}
                label="Modo de estimación fiscal"
                onChange={(event) => {
                  const mode = event.target.value as PVProfessionalTaxEstimationMode;
                  setForm((current) => ({
                    ...current,
                    tax_estimation_mode: mode,
                    tax_reserve_basis: mode === 'france_dentist_bnc' ? 'disabled' : current.tax_reserve_basis,
                    tax_reserve_percent: mode === 'france_dentist_bnc' ? '0.00' : current.tax_reserve_percent,
                  }));
                }}
              >
                <MenuItem value="simple">Reserva porcentual simple</MenuItem>
                <MenuItem value="france_dentist_bnc">Francia · dentista liberal BNC (estimación)</MenuItem>
              </Select>
            </FormControl>

            {form.tax_estimation_mode === 'france_dentist_bnc' ? (
              <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: alpha(CYAN, .025), border: `1px solid ${alpha(CYAN, .14)}` }}>
                <Typography sx={{ color: '#e2e8f0', fontWeight: 850, fontSize: '.86rem', mb: 1.35 }}>
                  Parámetros Francia
                </Typography>
                <Stack spacing={1.4}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
                    <FormControl fullWidth>
                      <InputLabel>Base de ingresos para fiscalidad</InputLabel>
                      <Select
                        value={form.projection_method}
                        label="Base de ingresos para fiscalidad"
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          projection_method: event.target.value as PVProfessionalProjectionMethod,
                        }))}
                      >
                        <MenuItem value="manual_target">Usar objetivo anual</MenuItem>
                        <MenuItem value="adaptive">Usar proyección dinámica</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Objetivo anual de ingresos (€)"
                      inputProps={{ inputMode: 'decimal' }}
                      value={form.annual_revenue_target}
                      onChange={(event) => setForm((current) => ({ ...current, annual_revenue_target: event.target.value }))}
                      helperText="Es tu referencia inicial. La aplicación no la cambia: compara el objetivo con la evolución real y te avisa si conviene revisar la base fiscal."
                    />
                    <TextField
                      fullWidth type="number" label="Año del objetivo"
                      inputProps={{ min: 2000, max: 2100, inputMode: 'numeric' }}
                      value={form.annual_revenue_target_year ?? ''}
                      onChange={(event) => setForm((current) => ({ ...current, annual_revenue_target_year: event.target.value ? Number(event.target.value) : null }))}
                      helperText="La previsión de un año no se reutiliza en otro sin revisarla."
                    />
                  </Stack>

                  <TextField
                    fullWidth
                    label="Gastos profesionales futuros adicionales (€)"
                    inputProps={{ inputMode: 'decimal' }}
                    value={form.additional_planned_professional_expenses}
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      additional_planned_professional_expenses: event.target.value,
                    }))}
                    helperText="Solo para gastos previstos que todavía no estén creados en Planificación. Los gastos profesionales planificados se incorporan automáticamente."
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
                    <FormControl fullWidth>
                      <InputLabel>Tratamiento IVA</InputLabel>
                      <Select
                        value={form.vat_treatment}
                        label="Tratamiento IVA"
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          vat_treatment: event.target.value as PVProfessionalVatTreatment,
                        }))}
                      >
                        <MenuItem value="exempt_healthcare">Odontología sanitaria exenta</MenuItem>
                        <MenuItem value="other">Otro / pendiente de confirmar</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Situación conventionné</InputLabel>
                      <Select
                        value={form.france_convention_status}
                        label="Situación conventionné"
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          france_convention_status: event.target.value as PVProfessionalFranceConventionStatus,
                        }))}
                      >
                        <MenuItem value="unknown">No lo sé todavía</MenuItem>
                        <MenuItem value="conventionne">Conventionné</MenuItem>
                        <MenuItem value="non_conventionne">Non conventionné</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
                    <TextField fullWidth label="Situación familiar" value="Soltera" disabled />
                    <TextField fullWidth label="Hijos / personas a cargo" value="0" disabled />
                  </Stack>

                  <TextField
                    label="Otros ingresos imponibles anuales del hogar (€)"
                    inputProps={{ inputMode: 'decimal' }}
                    value={form.other_taxable_income_annual}
                    onChange={(event) => setForm((current) => ({ ...current, other_taxable_income_annual: event.target.value }))}
                    helperText="Déjalo en 0 si no hay otros ingresos que debamos incorporar a la estimación del IR."
                  />
                </Stack>
              </Box>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>Base de la reserva orientativa</InputLabel>
                  <Select
                    value={form.tax_reserve_basis}
                    label="Base de la reserva orientativa"
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      tax_reserve_basis: event.target.value as PVProfessionalTaxReserveBasis,
                      tax_reserve_percent: event.target.value === 'disabled' ? '0.00' : current.tax_reserve_percent,
                    }))}
                  >
                    {(Object.keys(basisLabel) as PVProfessionalTaxReserveBasis[]).map((value) => (
                      <MenuItem key={value} value={value}>{basisLabel[value]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {form.tax_reserve_basis !== 'disabled' && (
                  <TextField
                    label="Porcentaje a reservar (%)"
                    inputProps={{ inputMode: 'decimal' }}
                    value={form.tax_reserve_percent}
                    onChange={(event) => setForm((current) => ({ ...current, tax_reserve_percent: event.target.value }))}
                    helperText="Regla de planificación definida por ti; no es un tipo fiscal legal."
                  />
                )}
              </>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
              <TextField
                fullWidth
                label="Compromiso fijo mensual"
                value={form.fixed_monthly_commitment_label ?? ''}
                onChange={(event) => setForm((current) => ({ ...current, fixed_monthly_commitment_label: event.target.value }))}
                placeholder="Ej. Cuota o seguro profesional"
              />
              <TextField
                fullWidth
                label="Importe fijo (€)"
                inputProps={{ inputMode: 'decimal' }}
                value={form.fixed_monthly_commitment_amount}
                onChange={(event) => setForm((current) => ({ ...current, fixed_monthly_commitment_amount: event.target.value }))}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Cuenta predeterminada para reservas</InputLabel>
              <Select
                value={form.default_reserve_account_id ?? ''}
                label="Cuenta predeterminada para reservas"
                onChange={(event) => setForm((current) => ({
                  ...current,
                  default_reserve_account_id: event.target.value || null,
                }))}
              >
                <MenuItem value="">Sin predeterminada</MenuItem>
                {activeAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} · {account.financial_scope === 'professional' ? 'Profesional' : 'Personal'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notas"
              multiline
              minRows={2}
              value={form.notes ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            />

            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saveSettingsMutation.isPending} onClick={() => setSettingsOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={saveSettingsMutation.isPending} onClick={saveSettings}>
            {saveSettingsMutation.isPending ? 'Guardando...' : 'Guardar configuración'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reserveOpen}
        maxWidth="sm"
        fullWidth
        onClose={() => !createReservesMutation.isPending && setReserveOpen(false)}
      >
        <DialogTitle>{franceMode ? 'Sincronizar reserva fiscal' : `Crear reservas sugeridas · ${month}`}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: .5 }}>
            Una reserva reduce la disponibilidad estimada, pero no mueve dinero ni equivale a un pago real.
          </Alert>
          <Stack spacing={1.5}>
            <FormControl fullWidth>
              <InputLabel>Cuenta asignada</InputLabel>
              <Select
                value={reserveAccountId}
                label="Cuenta asignada"
                onChange={(event) => setReserveAccountId(event.target.value)}
              >
                {activeAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} · {account.financial_scope === 'professional' ? 'Profesional' : 'Personal'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Fecha prevista"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={reserveDueDate}
              onChange={(event) => setReserveDueDate(event.target.value)}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant={includeTax ? 'contained' : 'outlined'}
                disabled={Number(control?.suggested_tax_reserve ?? 0) <= 0}
                onClick={() => setIncludeTax((value) => !value)}
              >
                {franceMode ? 'Fiscal + social' : 'Fiscal'} {euro(control?.suggested_tax_reserve ?? 0)}
              </Button>
              <Button
                variant={includeFixed ? 'contained' : 'outlined'}
                disabled={Number(control?.fixed_monthly_commitment_amount ?? 0) <= 0}
                onClick={() => setIncludeFixed((value) => !value)}
              >
                Fijo {euro(control?.fixed_monthly_commitment_amount ?? 0)}
              </Button>
            </Stack>
            <Typography sx={{ color: '#94a3b8', fontSize: '.78rem' }}>
              {franceMode
                ? 'La reserva fiscal anual pendiente se actualiza en lugar de duplicarse mientras siga pendiente.'
                : 'Si vuelves a generar el mismo mes, la aplicación detectará las reservas ya creadas y no las duplicará.'}
            </Typography>
            {reserveError && <Alert severity="error">{reserveError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={createReservesMutation.isPending} onClick={() => setReserveOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={createReservesMutation.isPending || !reserveAccountId || !reserveDueDate || (!includeTax && !includeFixed)}
            onClick={() => createReservesMutation.mutate()}
          >
            {createReservesMutation.isPending
              ? 'Guardando...'
              : franceMode
                ? 'Sincronizar reserva'
                : 'Crear reservas'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
